import React from 'react'
import PropTypes from 'prop-types'

import { List, ListItem } from '@sainsburys-tech/luna-list'

import FormOption from './FormOption'
import FormGroup from './FormGroup'
import propTypes from './utils/fieldPropTypes'

const getChecked = ({ optionGroupValue, optionValue, optionChecked }) => {
  let checked
  if (optionGroupValue !== undefined) checked = optionValue === optionGroupValue
  if (optionChecked !== undefined) checked = optionChecked
  return checked
}

const FormOptionFieldInput = ({ option, optionGroupValue, ...props }) => (
  <FormOption
    {...props}
    {...option}
    checked={getChecked({
      optionGroupValue,
      optionValue: option.value,
      optionChecked: option.checked,
    })}
  />
)

FormOptionFieldInput.propTypes = {
  option: PropTypes.shape({
    label: PropTypes.node,
    value: PropTypes.string,
    checked: PropTypes.bool,
  }).isRequired,
  optionGroupValue: PropTypes.string,
}

FormOptionFieldInput.defaultProps = {
  optionGroupValue: undefined,
}

FormOptionFieldInput.displayName = 'FormOptionFieldInput'

const FormOptionField = ({
  className,
  element,
  name,
  required,
  requiredLabel,
  optional,
  validationFirst,
  label,
  labelElement,
  hideLabel,
  error,
  warning,
  info,
  options,
  optionType,
  listType,
  outlined,
  ...props
}) => {
  const optionGroupValue =
    // eslint-disable-next-line react/destructuring-assignment
    props.value !== undefined ? props.value.toString() : undefined

  const optionInputProps = {
    ...props,
    type: optionType,
  }

  return (
    <FormGroup
      className={className}
      element={element}
      error={error}
      hideLabel={hideLabel}
      info={info}
      label={label}
      labelElement={labelElement}
      name={name}
      optional={optional}
      required={required}
      validationFirst={validationFirst}
      warning={warning}
    >
      {inputProps =>
        options.length > 1 ? (
          <List type={listType} spaced={outlined && 'sm'}>
            {options.map(option => (
              <ListItem type={listType} key={`${name}-${option.value}`}>
                <FormOptionFieldInput
                  {...inputProps}
                  {...optionInputProps}
                  option={option}
                  optionGroupValue={optionGroupValue}
                  innerRef={option.innerRef}
                  outlined={outlined}
                />
              </ListItem>
            ))}
          </List>
        ) : (
          <FormOptionFieldInput
            {...inputProps}
            {...optionInputProps}
            option={options[0]}
            optionGroupValue={optionGroupValue}
            innerRef={options[0].innerRef}
            outlined={outlined}
          />
        )
      }
    </FormGroup>
  )
}

FormOptionField.propTypes = {
  ...propTypes,
  /** Allows the top-level element to be customized */
  element: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.func,
    PropTypes.string,
  ]),
  /** The list of options to be made available */
  options: PropTypes.arrayOf(PropTypes.object).isRequired,
  /** What format the options should take */
  optionType: PropTypes.oneOf(['radio', 'checkbox', 'switch']),
  labelElement: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.func,
    PropTypes.string,
  ]),
  /** How the group of options should be displayed, e.g. inline */
  listType: PropTypes.string,
  /** Whether the option should have a border */
  outlined: PropTypes.bool,
}

FormOptionField.defaultProps = {
  element: 'fieldset',
  optionType: 'radio',
  labelElement: 'legend',
  listType: 'bare',
  outlined: false,
}

FormOptionField.displayName = 'FormOptionField'

export default FormOptionField
